import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useToast } from '../../context/ToastContext'
import { useBrokenImageHttpStatus } from '../../hooks/useBrokenImageHttpStatus'
import { useDeferUntilVisible } from '../../hooks/useDeferUntilVisible'
import { useImageInteractionPolicy } from '../../hooks/useImageInteractionPolicy'
import { useTouchLongPress } from '../../hooks/useLongPress'
import { toastForBrowserImageDownload, triggerBrowserImageDownload } from '../../lib/clientImageDownload'
import { resolveMediaImage } from '../../lib/resolvedMediaImage'
import {
  buildImageContextMenuItems,
  ImageContextDesktopMenu,
  ImageContextMobileSheet,
  ImageMoreActionsTrigger,
  useImageContextPayload,
} from '../ImageContextActions'
import { ImageLoadFailurePlaceholder } from '../ImageLoadFailurePlaceholder'
import { diagnoseImageLoadError } from '@glyphic/shared-core'

export type TimelineLibraryDot = 'in' | 'out'

/**
 * 时间线预览图：外层使用 params 快照的 CSS aspect-ratio。
 * 图片 object-cover 铺满容器，避免 letterbox 留白（由外层 aspect/min-height 约束比例）。
 *
 * scrollRootEl：横向滚动 strip 的根节点（state 传入）；deferSrc：延后挂载 src，适配嵌套滚动懒加载。
 * libraryDot：右上角状态标（圆润主题为圆点，直角主题为方块）；superResolutionOk：左下角「高清」衬底标签。
 * angular：与全局 pixel/geek 主题一致，控制圆角与线宽。
 */
export function TimelineAspectImage({
  src,
  className,
  cornerClass,
  style,
  scrollRootEl,
  deferSrc = true,
  angular = false,
  libraryDot,
  superResolutionOk,
  onPreview,
  /** 保存下载时的文件名前缀（时间线可传轮次/条目 id 片段） */
  downloadNameHint,
}: {
  src: string
  /** 含边框、背景、min-height、overflow、isolate；圆角与本字段一致时需同时传入 */
  className: string
  /** 与时间线外壳相同（rounded-2xl | rounded-none），铺在按钮/img/占位层上，避免横向滚动条合成层下圆角裁切闪烁成直角 */
  cornerClass: string
  style?: CSSProperties
  /** 横向滚动容器 DOM（由 TimelineRoundStrip 等向上注入） */
  scrollRootEl?: HTMLElement | null
  /** false：首张图等立即请求 */
  deferSrc?: boolean
  /** pixel/geek：直角 + 粗边框 */
  angular?: boolean
  /** 图库关联状态圆点；不传则不绘制（如图库 catalog 尚未就绪） */
  libraryDot?: TimelineLibraryDot
  /** 该张图输出侧标记为超清成功（与 metadata perImageSuccess 对齐） */
  superResolutionOk?: boolean
  onPreview?: () => void
  downloadNameHint?: string
}) {
  const [loaded, setLoaded] = useState(false)
  const [broken, setBroken] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const { showToast } = useToast()
  const policy = useImageInteractionPolicy()
  const ctx = useImageContextPayload()

  const { wrapRef, active: allowSrc } = useDeferUntilVisible({
    rootEl: scrollRootEl,
    defer: deferSrc,
    rootMargin: '96px',
  })

  const media = useMemo(() => resolveMediaImage(src), [src])
  const effectiveSrc = allowSrc ? media.displayUrl : undefined
  const brokenHttpStatus = useBrokenImageHttpStatus(effectiveSrc, broken)
  const brokenDiagnosis = useMemo(
    () => diagnoseImageLoadError({ url: effectiveSrc, httpStatus: brokenHttpStatus }),
    [effectiveSrc, brokenHttpStatus],
  )

  const trySave = useCallback(async () => {
    if (!effectiveSrc) return
    const stem = downloadNameHint?.trim().slice(0, 120) || `glyphic_${Date.now()}`
    try {
      const mode = await triggerBrowserImageDownload(effectiveSrc, stem)
      showToast(toastForBrowserImageDownload(mode))
    } catch (e) {
      showToast({
        message: e instanceof Error ? e.message : '保存失败',
        variant: 'error',
      })
    }
  }, [effectiveSrc, downloadNameHint, showToast])

  const fallbackStem = useMemo(
    () => downloadNameHint?.trim().slice(0, 120) || `glyphic_${Date.now()}`,
    [downloadNameHint],
  )

  const menuItems = useMemo(() => {
    const payload = ctx.desktop ?? ctx.sheet
    if (!payload?.src) return []
    return buildImageContextMenuItems({
      imageUrl: payload.src,
      onSave: async () => {
        try {
          const mode = await triggerBrowserImageDownload(payload.src, payload.stem)
          showToast(toastForBrowserImageDownload(mode))
        } catch (e) {
          showToast({
            message: e instanceof Error ? e.message : '保存失败',
            variant: 'error',
          })
        }
      },
      showToast,
    })
  }, [ctx.desktop, ctx.sheet, showToast])

  const longPressSave = useTouchLongPress(() => void trySave(), {
    disabled: !effectiveSrc || !policy.syntheticLongPressSave,
  })

  useEffect(() => {
    if (!effectiveSrc) ctx.closeAll()
  }, [effectiveSrc, ctx])

  useEffect(() => {
    if (policy.useEmbeddedNativeImageMenus) ctx.closeAll()
  }, [policy.useEmbeddedNativeImageMenus, ctx])

  useEffect(() => {
    setReloadTick(0)
    setBroken(false)
    setLoaded(false)
    setNaturalSize(null)
  }, [src])

  const nativeWx = policy.useEmbeddedNativeImageMenus

  const computedStyle: CSSProperties = {
    ...style,
    ...(naturalSize ? { aspectRatio: `${naturalSize.w} / ${naturalSize.h}` } : {}),
  }

  return (
    <div ref={wrapRef} className={className} style={computedStyle}>
      {libraryDot ? (
        <span
          className={[
            'pointer-events-auto absolute right-2 top-2 z-[8] h-2 w-2 cursor-default ring-inset',
            angular ? 'rounded-none ring-2' : 'rounded-full ring-1',
            libraryDot === 'in'
              ? 'bg-[var(--accent)] ring-[color-mix(in_srgb,var(--accent)_38%,transparent)]'
              : 'bg-[color-mix(in_srgb,var(--ink)_14%,var(--line)_58%)] ring-[color-mix(in_srgb,var(--line)_62%,transparent)]',
          ].join(' ')}
          aria-label={libraryDot === 'in' ? '该图已存入图库' : '该图尚未存入图库'}
          role="img"
        />
      ) : null}
      {superResolutionOk ? (
        <span
          className={[
            'pointer-events-none absolute bottom-2 left-2 z-[7] select-none px-1.5 py-0.5 text-[9px] font-semibold leading-none',
            angular ? 'rounded-none border-2' : 'rounded-md border',
            'border-[color-mix(in_srgb,var(--line)_62%,transparent)]',
            'bg-[color-mix(in_srgb,var(--panel)_94%,transparent)] text-ink',
            'backdrop-blur-[8px]',
            'shadow-[0_1px_3px_color-mix(in_srgb,var(--ink)_16%,transparent)]',
          ].join(' ')}
        >
          高清
        </span>
      ) : null}
      {!nativeWx ? (
        <button
          type="button"
          disabled={broken}
          className={[
            'absolute inset-0 z-[3] m-0 overflow-hidden border-0 bg-transparent p-0',
            broken ? 'cursor-default pointer-events-none' : 'cursor-zoom-in',
            cornerClass,
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
          ].join(' ')}
          aria-label="点击放大查看"
          onClick={onPreview}
          onContextMenu={
            effectiveSrc && policy.hijackContextMenu
              ? (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  ctx.openDesktop(e, media.displayUrl, fallbackStem)
                }
              : undefined
          }
          {...longPressSave}
        />
      ) : null}
      {effectiveSrc && !policy.hijackContextMenu && !nativeWx ? (
        <div className="pointer-events-none absolute bottom-2 right-2 z-[8] flex justify-end">
          <div className="pointer-events-auto">
            <ImageMoreActionsTrigger
              angular={angular}
              label="时间线缩略图更多操作"
              onOpen={() => ctx.openSheet(media.displayUrl, fallbackStem)}
            />
          </div>
        </div>
      ) : null}
      {broken ? (
        <ImageLoadFailurePlaceholder
          angular={angular}
          compact
          httpStatus={brokenHttpStatus}
          reason={brokenDiagnosis.reasonShort}
          urlLabel={brokenDiagnosis.safeUrlLabel}
          onReload={() => {
            setReloadTick((t) => t + 1)
            setBroken(false)
            setLoaded(false)
          }}
          className={['pointer-events-auto absolute inset-0 z-[12] justify-center overflow-hidden', cornerClass].join(
            ' ',
          )}
        />
      ) : (
        <img
          key={reloadTick}
          src={effectiveSrc}
          alt={nativeWx ? '生成图，点击预览' : ''}
          decoding="async"
          loading={deferSrc ? 'lazy' : 'eager'}
          onLoad={(e) => {
            const img = e.currentTarget
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
            }
            setLoaded(true)
          }}
          onError={() => {
            setBroken(true)
            setLoaded(true)
          }}
          onClick={nativeWx ? onPreview : undefined}
          onKeyDown={
            nativeWx && onPreview
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onPreview()
                  }
                }
              : undefined
          }
          role={nativeWx && onPreview ? 'button' : undefined}
          tabIndex={nativeWx && onPreview ? 0 : undefined}
          className={[
            'absolute inset-0 h-full w-full overflow-hidden object-cover transition-opacity duration-300',
            cornerClass,
            loaded ? 'opacity-100' : 'opacity-0',
            nativeWx
              ? 'z-[6] cursor-zoom-in pointer-events-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-transparent'
              : 'z-[2] pointer-events-none',
          ].join(' ')}
        />
      )}
      <ImageContextDesktopMenu
        angular={angular}
        open={ctx.desktop != null}
        anchor={
          ctx.desktop ? { x: ctx.desktop.x, y: ctx.desktop.y } : { x: 0, y: 0 }
        }
        items={menuItems}
        onClose={ctx.closeAll}
      />
      {!nativeWx ? (
        <ImageContextMobileSheet
          angular={angular}
          open={ctx.sheet != null}
          title="图片操作"
          items={menuItems}
          onClose={ctx.closeAll}
        />
      ) : null}
    </div>
  )
}
